// Billing detail page — invoice info, payment status, related patient + SR.

import { useParams, useNavigate, Link } from "react-router-dom";
import { useQuery } from "@tanstack/react-query";
import type { JSX } from "react";
import { toast } from "sonner";
import { apiFetch, apiFetchBlob } from "../lib/api.js";
import { Shell } from "../components/Shell.js";
import { STATUS_PILL, formatRupees, fmtDate, type InvoiceStatus } from "../lib/billing.js";
import { formatPatientRef } from "../lib/refs.js";

interface Invoice {
  _id: string;
  tenant_id: string;
  patient_id: string;
  sr_id: string | null;
  invoice_number: string;
  amount_paise: number;
  currency: string;
  status: InvoiceStatus;
  issued_at: string | null;
  due_date: string | null;
  paid_at: string | null;
  payment_method: string | null;
  notes: string;
  created_at: string;
  updated_at: string;
  // docs/24 — nsofar-rendered artifacts (present on agency-invoicing invoices).
  nsofar_invoice_number?: string | null;
  invoice_pdf_url?: string | null;
  invoice_view_url?: string | null;
  receipt_url?: string | null;
}

// Fetch the nsofar-rendered PDF via the HMAC passthrough (docs/24 §4b) with auth, then download.
async function downloadArtifact(id: string, kind: "invoice-pdf" | "receipt-pdf", filename: string): Promise<void> {
  try {
    const blob = await apiFetchBlob(`/billing/invoices/${id}/${kind}`);
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url; a.download = filename; a.click();
    URL.revokeObjectURL(url);
  } catch (e) {
    toast.error(e instanceof Error ? e.message : "Could not fetch the document.");
  }
}

// docs/24 §14 — nsofar invoice/receipt artifacts; only present on agency-invoicing invoices.
function DocumentsCard({ inv }: { inv: Invoice }): JSX.Element | null {
  if (!inv.nsofar_invoice_number) return null;
  const paid = inv.status === "paid" || inv.status === "refunded";
  function copyFamilyLink(): void {
    if (inv.invoice_view_url) void navigator.clipboard.writeText(inv.invoice_view_url).then(() => toast.success("Family billing link copied"));
  }
  return (
    <div className="bg-white rounded-xl shadow-sm border border-slate-100 p-5 mb-4">
      <p className="text-xs font-semibold text-slate-500 uppercase tracking-wide mb-3">Documents</p>
      <div className="flex flex-wrap gap-2">
        <button type="button" onClick={() => void downloadArtifact(inv._id, "invoice-pdf", `${inv.invoice_number}.pdf`)}
          className="px-3 py-1.5 text-xs rounded-lg border border-navy-700 text-navy-700 font-medium hover:bg-navy-50">
          Download invoice PDF
        </button>
        <button type="button" disabled={!paid} title={paid ? "" : "Available once paid"}
          onClick={() => void downloadArtifact(inv._id, "receipt-pdf", `${inv.invoice_number}-receipt.pdf`)}
          className="px-3 py-1.5 text-xs rounded-lg border border-slate-300 text-slate-700 font-medium hover:bg-slate-50 disabled:opacity-50">
          Download receipt
        </button>
        {inv.invoice_view_url && (
          <button type="button" onClick={copyFamilyLink}
            className="px-3 py-1.5 text-xs rounded-lg text-navy-700 font-medium hover:bg-navy-50">
            Copy family billing link
          </button>
        )}
      </div>
      <p className="text-xs text-slate-400 mt-2">nsofar #{inv.nsofar_invoice_number} — rendered by nsofar.</p>
    </div>
  );
}

function InfoRow({ label, value }: { label: string; value: string }): JSX.Element {
  return (
    <div className="flex gap-2">
      <span className="w-44 text-xs text-slate-500 shrink-0">{label}</span>
      <span className="text-sm text-slate-800">{value || "—"}</span>
    </div>
  );
}

function InvoiceInfoCard({ inv }: { inv: Invoice }): JSX.Element {
  return (
    <div className="bg-white rounded-xl shadow-sm border border-slate-100 p-5 mb-4">
      <div className="flex items-start justify-between mb-3">
        <div>
          <h2 className="text-xl font-semibold text-slate-900 font-mono">{inv.invoice_number}</h2>
          <p className="text-2xl text-slate-900 mt-1 tabular-nums">{formatRupees(inv.amount_paise)}</p>
        </div>
        <span className={`inline-block px-2.5 py-1 rounded-full text-xs font-medium ${STATUS_PILL[inv.status]}`}>
          {inv.status}
        </span>
      </div>
      <div className="space-y-2">
        <InfoRow label="Currency" value={inv.currency} />
      </div>
    </div>
  );
}

function PaymentCard({ inv }: { inv: Invoice }): JSX.Element {
  return (
    <div className="bg-white rounded-xl shadow-sm border border-slate-100 p-5 mb-4">
      <p className="text-xs font-semibold text-slate-500 uppercase tracking-wide mb-3">Payment</p>
      <div className="space-y-2">
        <InfoRow label="Issued" value={fmtDate(inv.issued_at)} />
        <InfoRow label="Due" value={fmtDate(inv.due_date)} />
        <InfoRow label="Paid" value={fmtDate(inv.paid_at)} />
        <InfoRow label="Method" value={inv.payment_method ?? ""} />
      </div>
    </div>
  );
}

function RelatedCard({ inv }: { inv: Invoice }): JSX.Element {
  return (
    <div className="bg-white rounded-xl shadow-sm border border-slate-100 p-5 mb-4">
      <p className="text-xs font-semibold text-slate-500 uppercase tracking-wide mb-3">Related</p>
      <div className="space-y-2">
        <div className="flex gap-2">
          <span className="w-44 text-xs text-slate-500 shrink-0">Patient</span>
          <Link to={`/patients/${inv.patient_id}`} className="text-sm text-navy-700 hover:underline">
            {formatPatientRef({ _id: inv.patient_id })}
          </Link>
        </div>
        {inv.sr_id && (
          <div className="flex gap-2">
            <span className="w-44 text-xs text-slate-500 shrink-0">Service request</span>
            <Link to={`/sr/${inv.sr_id}`} className="text-sm text-navy-700 hover:underline">
              {inv.sr_id}
            </Link>
          </div>
        )}
        {inv.notes && (
          <div className="flex gap-2">
            <span className="w-44 text-xs text-slate-500 shrink-0">Notes</span>
            <span className="text-sm text-slate-800 whitespace-pre-wrap">{inv.notes}</span>
          </div>
        )}
      </div>
    </div>
  );
}

export function BillingDetailPage(): JSX.Element {
  const { id = "" } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const { data: invoice, isLoading, isError } = useQuery<Invoice>({
    queryKey: ["invoice", id],
    queryFn: () => apiFetch<Invoice>(`/billing/invoices/${id}`),
  });

  if (isLoading) return <Shell><p className="p-8 text-sm text-slate-400" aria-busy="true">Loading…</p></Shell>;
  if (isError || !invoice) return <Shell><p className="p-8 text-sm text-red-600">Invoice not found.</p></Shell>;

  return (
    <Shell>
      <div className="max-w-5xl mx-auto px-4 py-6">
        <div className="flex items-center justify-between mb-4">
          <div className="flex items-center gap-3">
            <button
              onClick={() => navigate("/billing", { state: { highlightId: invoice._id } })}
              className="text-sm text-navy-700 hover:underline"
            >
              ← Billing
            </button>
            <h1 className="text-xl font-semibold text-slate-900 font-mono">{invoice.invoice_number}</h1>
          </div>
        </div>
        <InvoiceInfoCard inv={invoice} />
        <PaymentCard inv={invoice} />
        <DocumentsCard inv={invoice} />
        <RelatedCard inv={invoice} />
      </div>
    </Shell>
  );
}
